import { type FormEvent, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { api, jsonBody } from "../api/client";
import { BrandLockup } from "../brand/BrandMark";
import { LegalLine } from "../legal/LegalFooter";
import { ErrorPanel, Field, Notice } from "../ui/components";
import { useAuth } from "./AuthProvider";

type LoginStage = "credentials" | "authenticator" | "recovery";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stage, setStage] = useState<LoginStage>("credentials");
  const [challengeExpiresAt, setChallengeExpiresAt] = useState("");
  const [error, setError] = useState<unknown>();
  const [submitting, setSubmitting] = useState(false);
  const [ssoSubmitting, setSSOSubmitting] = useState(false);

  const requestedTarget = (location.state as { from?: string } | null)?.from;
  if (auth.session) {
    return (
      <Navigate
        to={requestedTarget ?? (auth.session.selectedMembership ? "/" : "/select-tenant")}
        replace
      />
    );
  }

  function finish(session: Awaited<ReturnType<typeof auth.completeMFA>>) {
    navigate(requestedTarget ?? (session.selectedMembership ? "/" : "/select-tenant"), {
      replace: true,
    });
  }

  async function submitCredentials(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(undefined);
    const data = new FormData(event.currentTarget);
    try {
      const result = await auth.signIn(
        String(data.get("email") ?? ""),
        String(data.get("password") ?? ""),
      );
      if (result.mfaRequired) {
        setChallengeExpiresAt(result.expiresAt);
        setStage("authenticator");
      } else {
        finish(result.session);
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  async function submitSSO(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSSOSubmitting(true);
    setError(undefined);
    const data = new FormData(event.currentTarget);
    try {
      const result = await api<{ url: string }>("/api/v1/auth/sso/start", {
        method: "POST",
        body: jsonBody({ email: String(data.get("ssoEmail") ?? "") }),
      });
      window.location.assign(result.url);
    } catch (caught) {
      setError(caught);
      setSSOSubmitting(false);
    }
  }

  async function submitMFA(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(undefined);
    const data = new FormData(event.currentTarget);
    try {
      const session = await auth.completeMFA(
        stage === "recovery"
          ? { recoveryCode: String(data.get("recoveryCode") ?? "") }
          : { code: String(data.get("code") ?? "") },
      );
      finish(session);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-layout">
      <section className="auth-brand">
        <BrandLockup />
        <div>
          <h1>Every space. Every event. One clear view.</h1>
          <p>
            Secure operations for venue teams, from first inquiry to payment, inventory,
            sponsorship, and reporting.
          </p>
        </div>
      </section>
      <section className="auth-panel" aria-labelledby="sign-in-title">
        <div className="auth-card">
          <h1 id="sign-in-title">{stage === "credentials" ? "Welcome back" : "Verify it’s you"}</h1>
          <p>
            {stage === "credentials"
              ? "Sign in to your staff, customer, or platform account."
              : "Your password is correct. Enter the code from your authenticator app to finish signing in."}
          </p>
          {new URLSearchParams(location.search).get("sso") === "failed" ? (
            <Notice tone="danger">
              Work SSO could not be completed. Try again or contact your organization owner.
            </Notice>
          ) : null}
          {error ? <ErrorPanel error={error} /> : null}
          {stage === "credentials" ? (
            <div className="stack">
              <form onSubmit={submitCredentials}>
                <Field label="Email address">
                  <input name="email" type="email" autoComplete="username" required />
                </Field>
                <Field label="Password">
                  <input name="password" type="password" autoComplete="current-password" required />
                </Field>
                <button className="button primary" type="submit" disabled={submitting}>
                  {submitting ? "Signing in…" : "Sign in"}
                </button>
                <Link className="button ghost" to="/forgot-password">
                  Forgot password?
                </Link>
              </form>
              <div className="auth-divider">
                <span>or</span>
              </div>
              <form onSubmit={submitSSO}>
                <Field
                  label="Work email for SSO"
                  hint="We’ll route you to your organization’s approved identity provider."
                >
                  <input name="ssoEmail" type="email" autoComplete="username" required />
                </Field>
                <button className="button secondary" type="submit" disabled={ssoSubmitting}>
                  {ssoSubmitting ? "Finding provider…" : "Continue with work SSO"}
                </button>
              </form>
              <Link className="button ghost" to="/">
                Back to product overview
              </Link>
            </div>
          ) : (
            <form onSubmit={submitMFA}>
              {challengeExpiresAt ? (
                <Notice tone="info">
                  For your security, finish signing in before{" "}
                  {new Date(challengeExpiresAt).toLocaleTimeString()}.
                </Notice>
              ) : null}
              {stage === "authenticator" ? (
                <Field label="Authenticator code" hint="Enter the current six-digit code.">
                  <input
                    name="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                  />
                </Field>
              ) : (
                <Field label="Recovery code" hint="Each recovery code can only be used once.">
                  <input name="recoveryCode" autoComplete="one-time-code" required />
                </Field>
              )}
              <button className="button primary" type="submit" disabled={submitting}>
                {submitting ? "Verifying…" : "Verify and sign in"}
              </button>
              <button
                className="button secondary"
                type="button"
                onClick={() => {
                  setError(undefined);
                  setStage(stage === "authenticator" ? "recovery" : "authenticator");
                }}
              >
                {stage === "authenticator" ? "Use a recovery code" : "Use authenticator instead"}
              </button>
              <button
                className="button ghost"
                type="button"
                onClick={() => {
                  setError(undefined);
                  setStage("credentials");
                }}
              >
                Back to password
              </button>
            </form>
          )}
        </div>
        <LegalLine />
      </section>
    </main>
  );
}
